import { ListingStatus } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  CoreType,
  PlanetFilterInput,
  PlanetStage,
  PrimevalLegacyOptions
} from '@apeiron/planet'
import { Box, styled } from '@mui/material'
import {
  FilterCheckboxGroup,
  FilterCheckboxItem
} from '@src/components/planet/filter/filterCheckbox/FilterCheckboxGroup'
import { SingleFilterCheckboxGroup } from '@src/components/planet/filter/filterCheckbox/SingleFilterCheckboxGroups'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import ComponentBlocker from '@src/components/share/generic/ComponentBlocker'
import { ComponentBlock } from '@src/constants/componentBlock'
import * as R from 'ramda'
import React, { useCallback, useMemo } from 'react'
import { useTranslation, UseTranslationResponse } from 'react-i18next'

const PRIMEVAL_LEGACY_MIN = 1

const PRIMEVAL_LEGACY_MAX = 62

const DEFAULT_PRIMEVAL_LEGACY_VALUE = {
  primevalLegacyMin: undefined,
  primevalLegacyMax: undefined
}

const STAGE_ITEMS: FilterItem<string>[] = [
  {
    name: PlanetStage.Core,
    labelKey: 'filter.sections.others.stage.core',
    value: PlanetStage.Core
  },
  {
    name: PlanetStage.Planet,
    labelKey: 'filter.sections.others.stage.planet',
    value: PlanetStage.Planet
  }
]

const EXORDIUM_TYPE_ITEMS: FilterItem<number>[] = [
  {
    name: `exordium_${CoreType.Elemental}`,
    labelKey: 'filter.sections.others.exordium_type.elemental',
    value: CoreType.Elemental
  },
  {
    name: `exordium_${CoreType.Mythic}`,
    labelKey: 'filter.sections.others.exordium_type.mythic',
    value: CoreType.Mythic
  },
  {
    name: `exordium_${CoreType.Arcane}`,
    labelKey: 'filter.sections.others.exordium_type.arcane',
    value: CoreType.Arcane
  },
  {
    name: `exordium_${CoreType.Divine}`,
    labelKey: 'filter.sections.others.exordium_type.divine',
    value: CoreType.Divine
  },
  {
    name: `exordium_${CoreType.Primal}`,
    labelKey: 'filter.sections.others.exordium_type.primal',
    value: CoreType.Primal
  },
  {
    name: `exordium_${CoreType.None}`,
    labelKey: 'filter.sections.others.exordium_type.none',
    value: CoreType.None
  }
]

const SPECIAL_ITEMS: FilterItem<PrimevalLegacyOptions>[] = [
  {
    name: 'primevalLegacy',
    labelKey: 'filter.sections.others.special.primeval_legacy',
    value: {
      primevalLegacyMin: PRIMEVAL_LEGACY_MIN,
      primevalLegacyMax: PRIMEVAL_LEGACY_MAX
    }
  }
]

const SALE_ITEMS: FilterItem<ListingStatus>[] = [
  {
    name: 'forSale',
    labelKey: 'filter.sections.others.status.for_sale',
    value: ListingStatus.LISTING
  },
  {
    name: 'notForSale',
    labelKey: 'filter.sections.others.status.not_for_sale',
    value: ListingStatus.UNLISTING
  }
]

const getCheckboxItems = <T extends unknown>(
  t: UseTranslationResponse<'translation', undefined>['t'],
  items: FilterItem<T>[]
): FilterCheckboxItem<T>[] => {
  return items.map(item => ({
    name: item.name,
    value: item.value,
    label: t(item.labelKey)
  }))
}

type FilterItem<T> = {
  name: string
  labelKey: string
  value: T
}

export type OthersFilterSectionProps = {
  className?: string
  filterOptions: PlanetFilterInput
  onFilterOptionsChange: (input: PlanetFilterInput) => void
}

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding-left: 24px;
    padding-right: 10px;
    padding-top: 8px;
    overflow: hidden;
  }
`

const CheckboxGroupContainer = styled(Box)`
  padding-right: 5px;
`

const CheckboxStage = styled(SingleFilterCheckboxGroup<string>)`
  margin-top: 24px;
`

const CheckboxExordiumType = styled(FilterCheckboxGroup<number>)`
  margin-top: 24px;
`

const CheclboxSpecial = styled(
  SingleFilterCheckboxGroup<PrimevalLegacyOptions>
)`
  margin-top: 24px;
`

const CheckboxListing = styled(FilterCheckboxGroup<ListingStatus>)`
  margin-top: 24px;
`

export const OthersFilterSection: React.FC<
  OthersFilterSectionProps
> = props => {
  const { className, filterOptions, onFilterOptionsChange } = props

  const { config } = useFrontendConfigContext()

  const { t } = useTranslation()

  const numOfSkillCountSelect =
    filterOptions.activeCSkillCountMin != null ||
    filterOptions.activeCSkillCountMax != null
      ? 1
      : 0

  const numOfBreedCountSelect =
    filterOptions.breedCountMin != null || filterOptions.breedCountMax != null
      ? 1
      : 0

  const numOfBaseAgeSelect =
    filterOptions.ageMin != null || filterOptions.ageMax != null ? 1 : 0

  const numOfStageSelect =
    filterOptions.stage != null && filterOptions.stage.length > 0 ? 1 : 0

  const numOfCoreTypeSelect = filterOptions.coreType?.length
    ? filterOptions.coreType.length
    : 0

  const numOfPromevalLegacySelect =
    filterOptions.primevalLegacyMin != null &&
    filterOptions.primevalLegacyMax != null
      ? 1
      : 0

  const numOfStatusSelect = filterOptions.nftListingStatus
    ? filterOptions.nftListingStatus.length
    : 0

  const numFilters =
    numOfSkillCountSelect +
    numOfBreedCountSelect +
    numOfBaseAgeSelect +
    numOfStageSelect +
    numOfCoreTypeSelect +
    numOfPromevalLegacySelect +
    numOfStatusSelect

  const onReset = useCallback(() => {
    onFilterOptionsChange({
      ...filterOptions,
      activeCSkillCountMax: undefined,
      activeCSkillCountMin: undefined,
      ageMax: undefined,
      ageMin: undefined,
      breedCountMax: undefined,
      breedCountMin: undefined,
      stage: undefined,
      coreType: undefined,
      primevalLegacyMin: undefined,
      primevalLegacyMax: undefined,
      nftListingStatus: []
    })
  }, [filterOptions, onFilterOptionsChange])

  const onStageChange = useCallback(
    (value?: string) => {
      onFilterOptionsChange({
        ...filterOptions,
        stage: value ? [value] : undefined
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const onExordiumTypeChange = useCallback(
    (value?: number[]) => {
      onFilterOptionsChange({
        ...filterOptions,
        coreType: value
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const onSpecialChange = useCallback(
    (value?: PrimevalLegacyOptions) => {
      onFilterOptionsChange({
        ...filterOptions,
        primevalLegacyMin: value?.primevalLegacyMin,
        primevalLegacyMax: value?.primevalLegacyMax
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const onSaleChange = useCallback(
    (value?: ListingStatus[]) => {
      onFilterOptionsChange({
        ...filterOptions,
        nftListingStatus: value
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const stageItems = getCheckboxItems(t, STAGE_ITEMS)

  const exordiumTypeItems = getCheckboxItems(t, EXORDIUM_TYPE_ITEMS)

  const specialItems = getCheckboxItems(t, SPECIAL_ITEMS)

  const stageValue = filterOptions.stage ? filterOptions.stage[0] : undefined

  const primevalLegacyItem = SPECIAL_ITEMS[0]

  const saleItems = getCheckboxItems(t, SALE_ITEMS)

  const primevalLegacyValue =
    filterOptions.primevalLegacyMin === PRIMEVAL_LEGACY_MIN &&
    filterOptions.primevalLegacyMax === PRIMEVAL_LEGACY_MAX
      ? primevalLegacyItem.value
      : DEFAULT_PRIMEVAL_LEGACY_VALUE

  const ageMarks = useMemo(() => {
    if (!R.isNil(config)) {
      const marksCount = Math.round(
        Math.log(config.planet.maxAge) / Math.log(10)
      )

      const marks = []

      for (let i = 1; i < marksCount; i++) {
        marks.push({ value: i })
      }

      return marks
    }

    return []
  }, [config])

  const handleOnAgeScale = useCallback((sliderValue: number): number => {
    return sliderValue > 0 ? Math.round(Math.pow(10, sliderValue)) : 0
  }, [])

  const handleOnAgeDescale = useCallback((value: number): number => {
    if (value === 0) {
      return value
    }

    // speical case
    if (value === 1) {
      return 1 / 10
    }

    return Math.log(value) / Math.log(10)
  }, [])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('filter.sections.others.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numFilters} onReset={onReset} />
      </FilterSectionHeader>
      <Content>
        <BlurForm>
          {config && (
            <RangeSlider
              labelText={t('filter.sections.others.active_skill_count')}
              min={0}
              max={config.planet.maxActiveSkill}
              minValueKey='activeCSkillCountMin'
              maxValueKey='activeCSkillCountMax'
              filterOptions={filterOptions}
              onFilterOptionsChange={onFilterOptionsChange}
            />
          )}
          {config && (
            <RangeSlider
              labelText={t('filter.sections.others.breed_count')}
              min={0}
              max={config.breed.max}
              minValueKey='breedCountMin'
              maxValueKey='breedCountMax'
              filterOptions={filterOptions}
              onFilterOptionsChange={onFilterOptionsChange}
            />
          )}
          {config && (
            <RangeSlider
              filterOptions={filterOptions}
              labelText={t('filter.sections.others.base_age')}
              marks={ageMarks}
              max={R.length(ageMarks) + 1}
              maxValueKey='ageMax'
              min={0}
              minValueKey='ageMin'
              step={0.1}
              descale={handleOnAgeDescale}
              scale={handleOnAgeScale}
              onFilterOptionsChange={onFilterOptionsChange}
            />
          )}
        </BlurForm>
        <CheckboxGroupContainer>
          <ComponentBlocker blockKey={ComponentBlock.PlanetFilterStage}>
            <CheckboxStage
              label={t('filter.sections.others.stage.label')}
              items={stageItems}
              value={stageValue}
              onChange={onStageChange}
            />
          </ComponentBlocker>
          <CheckboxExordiumType
            label={t('filter.sections.others.exordium_type.label')}
            items={exordiumTypeItems}
            value={filterOptions.coreType ?? []}
            onChange={onExordiumTypeChange}
          />
          <CheclboxSpecial
            label={t('filter.sections.others.special.label')}
            items={specialItems}
            value={primevalLegacyValue}
            onChange={onSpecialChange}
          />
          <ComponentBlocker blockKey={ComponentBlock.PlanetFilterStatus}>
            <CheckboxListing
              label={t('filter.sections.others.status.label')}
              items={saleItems}
              value={filterOptions.nftListingStatus ?? []}
              onChange={onSaleChange}
            />
          </ComponentBlocker>
        </CheckboxGroupContainer>
      </Content>
    </FilterSection>
  )
}
